import React from 'react';
import {Paper, Grid, Box, Link} from '@mui/material';
import Colors from '../../../constants/Colors';
import { showNotificationBar } from '../../../actions/error-pages/NotificationBarActions';
import { isValidEmail } from '../../../utilities/Validation';
import { logProfileIn } from '../../../clients/profile/ProfileClient';
import { useState } from 'react';
import { isEmpty } from 'lodash';
import { getCurrentUser, setUserFromCognito } from '../../../actions/profile/UserActions';
import { useEffect } from 'react';
import { SESSION_REDIRECT_KEY, getSessionProperty } from '../../../utilities/SessionUtilities';
import {LoadingButton, TextButton} from "../../common/StyledButtons";
import {StyledTextField} from "../../common/StyledTextField";
import { DesktopBox, MobileBox } from "../../common/Boxes";
import background from "../../../assets/backgrounds/city-scape-background.webp";



const LoginForm = ({ history }) => {
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();

  const [videoLoaded, setVideoLoaded] = useState(false);

  const [errors, setErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkAndRestoreSession().then()
  }, [])

  const checkAndRestoreSession = async () => {
    try {
      const loggedInUser = await getCurrentUser();
      if (loggedInUser !== undefined) {
        history.push('/dashboard');
      }
    } catch (err) { /* do nothing */ }
  }

  const isValidForm = () => {
    const foundErrors = [];
    let reportedError;
    if (!username || isEmpty(username) || !isValidEmail(username)) {
       foundErrors.push('username');
       reportedError = 'Please provide a valid user name.'
    }
    if (!password || isEmpty(password)) {
       foundErrors.push('password');
       if (!reportedError) reportedError = 'Please provide a password'
    }
    setErrors(foundErrors);
    setErrorMessage(reportedError)
    return foundErrors.length === 0;
  }

  const handleRegisterOnClick = (e) => {
    e.stopPropagation();
    history.replace('/register');
  }

  const handleForgotPasswordOnClick = (e) => {
    e.stopPropagation();
    history.push('/reset-password');
  }

  const handleSignIn = async () => {
    if (!isValidForm()) return;
    setIsLoading(true)
    await logProfileIn(username, password)
    .then((result) => {
      setUserFromCognito(result);
      const redirect = getSessionProperty(SESSION_REDIRECT_KEY, true)
      if (redirect) history.push(redirect);
      else history.push('/dashboard')
    })
    .catch((err) => {
      if (err.name === 'NotAuthorizedException' || err.name === 'UserNotFoundException') {
        setErrors(['password'])
        setErrorMessage('The username and password combination is incorrect.')
      } else if(err.name === 'UserNotFoundException') {
        setErrors(['username'])
        setErrorMessage('This username is not registered. Create an account by clicking the button below.')
      } else if(err.name === 'UserNotConfirmedException') {
        history.push({
          pathname: '/verify',
          state: { user: { email: username, password }}
        });
      } else {
        showNotificationBar('error', 'Unable to log in at this time. Please try again in a few minutes.');
        console.error('error', err.name, err.message)
      }
    }).finally(() => setIsLoading(false));
  }

  const renderLoginContent = () => (
    <Box
      sx={{
        padding: '40px',
        backgroundColor: '#fff',
        width: '100%',
        maxWidth: '400px',
      }}
    >
      <h5 style={{ textAlign: 'center', margin: 0, fontSize: '14px' }}>BCK Business Manager</h5>
      <h3 style={{ textAlign: 'center', margin: 0, fontSize: '28px' }}>Sign In</h3>
      <p style={{ textAlign: 'center' }}>
        {errorMessage ? (
          <span style={{ color: Colors.redColor }}>{errorMessage}</span>
        ) : (
          'Please provide your credentials to access the Dashboard.'
        )}
      </p>
      <StyledTextField
        fullWidth
        autoFocus
        error={errors.includes('username')}
        onKeyPress={(e) => { if (e.key === 'Enter') handleSignIn(); }}
        value={username}
        type="text"
        label="E-Mail"
        onChange={(e) => setUsername(e.target.value)}
        sx={{ mt: 2 }}
      />
      <StyledTextField
        fullWidth
        error={errors.includes('password')}
        onKeyPress={(e) => { if (e.key === 'Enter') handleSignIn(); }}
        value={password}
        label="Password"
        type="password"
        onChange={(e) => setPassword(e.target.value)}
        sx={{ mt: 2 }}
      />
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <LoadingButton onClick={handleSignIn} disabled={isLoading} isLoading={isLoading}>
          Sign In
        </LoadingButton>
      </Box>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <TextButton onClick={handleForgotPasswordOnClick} disabled={isLoading}>
          Forgot Password
        </TextButton>
        <TextButton onClick={handleRegisterOnClick} disabled={isLoading}>
          Create an Account
        </TextButton>
      </Box>
    </Box>
  );

  const renderImageSection = () => (
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: Colors.primaryColor
        }}
      >
        {/* Background Image */}
        <video
          autoPlay
          muted
          loop
          playsInline
          onCanPlayThrough={() => setVideoLoaded(true)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: videoLoaded ? 'blur(0px)' : 'blur(10px)',
            opacity: videoLoaded ? 1 : 0,
            transition: 'filter 1.2s ease, opacity 1.2s ease',
          }}
        >
          <source src="https://s3.us-east-2.amazonaws.com/docs.consultwithkyle.com/assets/city-scape.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Semi-transparent Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#10AA00',
            opacity: 0.75,
            zIndex: 2,
          }}
        />

        {/* Centered Text Content */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'left',
            zIndex: 3,
            color: 'white',
            px: 2, // optional padding
          }}
        >
          <Box sx={{ minWidth: '200px' }}>
            <h3 style={{ color: '#fff', fontSize: '40px', marginBottom: 0, paddingBottom: 0 }}>Welcome back,<br />let's keep moving forward.</h3>
            <p style={{ fontSize: '18px' }}>Your spot for updates, insights, and support is a click away.</p>
          </Box>
        </Box>
      </Box>
    </Grid>
  )

  const renderFooterLinks = () => (
    <>
      <Link href="/terms-of-use" target="_blank" underline="hover" sx={{ mx: 1, fontSize: '0.875rem' }}>
        Terms of Use
      </Link>
      <Link href="/privacy-policy" target="_blank" underline="hover" sx={{ mx: 1, fontSize: '0.875rem' }}>
        Privacy Policy
      </Link>
    </>
  )
  
  
  return (
    <div>
      {/* Desktop Layout */}
      <DesktopBox sx={{ width: '100%', height: 'calc(100vh - 64px)' }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Left Side - Login Form */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fff',
                paddingX: '40px',
                paddingY: '20px',
                boxSizing: 'border-box',
                overflow: 'hidden', // prevent scroll
              }}
            >
              {/* Centered login content */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '400px',
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                {renderLoginContent()}
              </Box>

  
              {/* Footer links */}
              <Box sx={{ textAlign: 'center', paddingBottom: '10px' }}>
                {renderFooterLinks()}
              </Box>
            </Paper>
          </Grid>
  
          {/* Right Side - Image */}
          {renderImageSection()}
        </Grid>
      </DesktopBox>
  
      {/* Mobile Layout */}
      <MobileBox
        sx={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 80px)',
          backgroundColor: '#fff',
          padding: '20px',
        }}
      >
        {renderLoginContent()}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          {renderFooterLinks()}
        </Box>
      </MobileBox>
    </div>
  );
  
  
}

export default LoginForm;
